import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageHero from "@/components/PageHero";
import Sidebar from "@/components/Sidebar";
import { searchPosts } from "@/lib/api";
import { categoryStyle } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    } catch {
        return iso;
    }
}

function highlight(text, q) {
    if (!q || !text) return text;
    const safe = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${safe})`, "ig"));
    return parts.map((p, i) =>
        p.toLowerCase() === q.toLowerCase() ? (
            <mark
                key={i}
                className="bg-[#F5C53D] text-[#1B1B1B] px-0.5 rounded-sm"
            >
                {p}
            </mark>
        ) : (
            p
        ),
    );
}

export default function SearchResultsPage() {
    const [params, setParams] = useSearchParams();
    const q = params.get("q") || "";
    const [draft, setDraft] = useState(q);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setDraft(q);
        if (!q.trim()) {
            setResults([]);
            return;
        }
        setLoading(true);
        searchPosts(q)
            .then(setResults)
            .catch(() => setResults([]))
            .finally(() => setLoading(false));
    }, [q]);

    const submit = (e) => {
        e.preventDefault();
        const next = draft.trim();
        setParams(next ? { q: next } : {});
    };

    const lead = useMemo(() => {
        if (!q) return "Type a chef, restaurant, city, or topic above.";
        if (loading) return `Searching for "${q}"…`;
        if (results.length === 0) return `No matches for "${q}".`;
        return `${results.length} result${results.length === 1 ? "" : "s"} for "${q}".`;
    }, [q, loading, results.length]);

    return (
        <div data-testid="search-page">
            <PageHero
                eyebrow="Search"
                title={q ? `Results for "${q}"` : "Search BENNE"}
                lead={lead}
                breadcrumbs={[
                    { label: "Home", to: "/" },
                    { label: "Search" },
                ]}
            />

            <div className="px-4 md:px-8 lg:px-12 py-10">
                <form
                    onSubmit={submit}
                    className="flex items-center gap-2 max-w-2xl mb-10"
                    data-testid="search-form"
                >
                    <div className="flex-1 flex items-center gap-2 border border-black/20 px-4 py-2 bg-white">
                        <Search className="h-4 w-4 text-[#1B1B1B]/60" />
                        <Input
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            placeholder="Search posts, chefs, restaurants, categories…"
                            className="border-none shadow-none focus-visible:ring-0 px-0 h-9 font-sofia"
                            data-testid="search-input"
                        />
                    </div>
                    <Button
                        type="submit"
                        className="bg-[#E63B26] hover:bg-[#1B1B1B] text-white font-poppins tracking-[0.18em] uppercase rounded-none h-12 px-6"
                        data-testid="search-submit"
                    >
                        Search
                    </Button>
                </form>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-8">
                        {q && results.length === 0 && !loading && (
                            <p
                                className="font-sofia text-[#1B1B1B]/70"
                                data-testid="search-empty"
                            >
                                Try a different spelling, or browse{" "}
                                <Link
                                    to="/categories"
                                    className="text-[#E63B26] underline"
                                >
                                    every category
                                </Link>
                                .
                            </p>
                        )}

                        <ul className="space-y-6">
                            {results.map((p) => (
                                <li
                                    key={p.id}
                                    className="border-l-4 border-[#F5C53D] bg-white p-5 hover:shadow-md transition"
                                    data-testid={`search-result-${p.slug}`}
                                >
                                    <div className="flex flex-col md:flex-row gap-5">
                                        <Link
                                            to={`/post/${p.slug}`}
                                            className="md:w-48 shrink-0 aspect-[3/2] overflow-hidden bg-[#1B1B1B] block"
                                        >
                                            <img
                                                src={p.image_url}
                                                alt={p.title}
                                                className="w-full h-full object-cover hover:scale-105 transition duration-500"
                                                loading="lazy"
                                            />
                                        </Link>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <span
                                                    className="badge-category text-[0.62rem]"
                                                    style={categoryStyle(
                                                        p.category,
                                                    )}
                                                >
                                                    {p.category}
                                                </span>
                                                <span className="font-sofia text-xs text-[#1B1B1B]/60">
                                                    {formatDate(p.date)}
                                                </span>
                                            </div>
                                            <h3 className="mt-2 font-poppins font-bold text-lg md:text-xl leading-tight">
                                                <Link
                                                    to={`/post/${p.slug}`}
                                                    className="hover:text-[#E63B26] transition"
                                                    data-testid={`search-result-link-${p.slug}`}
                                                >
                                                    {highlight(p.title, q)}
                                                </Link>
                                            </h3>
                                            {p.sub_header && (
                                                <p className="mt-1 italic font-sofia text-[#1B1B1B]/80">
                                                    {highlight(
                                                        p.sub_header,
                                                        q,
                                                    )}
                                                </p>
                                            )}
                                            <p className="mt-2 font-sofia text-[#1B1B1B]/85 leading-relaxed line-clamp-3">
                                                {highlight(
                                                    p.blurb || p.body,
                                                    q,
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="lg:col-span-4">
                        <Sidebar />
                    </div>
                </div>
            </div>
        </div>
    );
}
